import {createFileRoute, notFound} from "@tanstack/react-router";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";
import {reportCollectionWithReportsMetadataZodValidator} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportCollectionsWorkspaceCapability/domain/ReportCollection";
import type {ImageDetails} from "@vesta/janus-compatibility-module/utilities/typeDefinitions";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {fetchDatasets} from "~/utilities/reports/reportsAnalyticsApi";
import type {DatasetMetadata} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/a/_adminScaffold/debugger/-types";
import {ADGPT__DATASETS__QUERY_KEY} from "~/utilities/queryKeys";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_defaultScaffold/collections/$collectionId",
)({
    loader: async ({context, params}) => {
        const collectionMetadataResult = await robustJsonResultClientFetch(
            `${context.intellsysBaseUrl}/sub-system/pluto/report-collections/get-by-id`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${context.authenticationStatus.accessToken}`,
                    "X-Workspace-Id": context.workspace.id,
                },
                body: JSON.stringify({
                    reportCollectionId: params.collectionId,
                }),
            },
            reportCollectionWithReportsMetadataZodValidator.nullable(),
        );
        if (collectionMetadataResult.success === false) {
            throw collectionMetadataResult.error;
        }
        const collectionMetadata = collectionMetadataResult.data;

        if (collectionMetadata === null) {
            return notFound();
        }

        const datasets = await context.queryClient.ensureQueryData<Array<DatasetMetadata>>({
            queryKey: [ADGPT__DATASETS__QUERY_KEY, context.workspace.id],
            queryFn: async () => {
                const result = await fetchDatasets({
                    intellsysBaseUrl: context.intellsysBaseUrl,
                    accessToken: context.authenticationStatus.accessToken,
                    workspaceId: context.workspace.id,
                });
                if (result.success === false) {
                    throw result.error;
                }
                return result.data;
            },
            retry: false,
            staleTime: Infinity,
        });

        const datasetLogoMap: Map<Uuid, ImageDetails | null> = new Map();
        for (const dataset of datasets) {
            datasetLogoMap.set(dataset.id, dataset.imageDetails);
        }

        return {
            workspace: context.workspace,
            collectionwithReportsMetadata: collectionMetadata,
            intellsysBaseUrl: context.intellsysBaseUrl,
            accessToken: context.authenticationStatus.accessToken,
            datasetLogoMap: datasetLogoMap,
        };
    },
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
